import { isEventKey } from "keycode";
import debounce from "lodash/debounce";

import { isBreakpoint } from "../utils";

const SELECTORS = {
  subNavItem: ".js-subnav__item",
  subNavLink: ".js-subnav__link",
  subNavPanel: ".js-subnav__content",
  // Selectors for keyboard accessibility.
  subNavItemLink: ".subnav__child-link",
  subNavItemActiveLink: ".subnav__item--active-link",
};

const CLASSES = {
  active: "subnav__item--active",
  subNavFirst: "subnav__item--haschilds",
  subNavLast: "subnav__item--last",
  subNavItemActiveLink: "subnav__item--active-link",
};

class SubNav {
  constructor(context) {
    this.context = context;
    this.subNavItems = null;
    this.isIddle = false;
    this.subMenuOpen = false;
  }

  init() {
    this.subNavItems = [...this.context.querySelectorAll(SELECTORS.subNavItem)];
    this.subscribe();
  }

  subscribe() {
    this.subNavItems.forEach((el) => {
      const trigger = el.querySelector(SELECTORS.subNavLink);
      const panel = el.querySelector(SELECTORS.subNavPanel);

      if (isBreakpoint("large")) {
        el.addEventListener("mouseover", () => this.openSubNav(panel, el));
        el.addEventListener("mouseout", () => this.closeSubNav(panel, el));
      }

      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleSubNav(panel, el);
      });

      el.addEventListener("keydown", (e) => {
        this.handleKeydown(e, panel, el);
        this.closeIfKey(e, panel, el);
      });

      window.addEventListener(
        "scroll",
        debounce((e) => {
          this.handleScroll(e, panel, el);
        }),
        300,
        {
          leading: true,
          trailing: false,
        },
      );
    });

    document.addEventListener("click", (e) => this.handleClickOutside(e));
  }

  handleScroll(event, panel, el) {
    if (!this.isIddle) {
      const currentScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;

      // Because of momentum scrolling on mobiles, we shouldn't continue if it is less than zero
      if (currentScrollPosition < 0) {
      }
    }
  }

  toggleSubNav(panel, parent) {
    if (panel.hasAttribute("hidden")) {
      this.subMenuOpen = false;
    }

    this.subMenuOpen
      ? this.closeSubNav(panel, parent)
      : this.openSubNav(panel, parent);
    return this;
  }

  closeIfKey(event, panel, parent) {
    const isFirstLink = event.target.parentNode.classList.contains(
      CLASSES.subNavFirst,
    );
    const isLastLink = event.target.parentNode.classList.contains(
      CLASSES.subNavLast,
    );
    const isTabPressed = isEventKey(event, "tab");
    const isEscPressed = isEventKey(event, "esc");
    const isShiftPressed = event.shiftKey;

    if (
      (!isShiftPressed && isLastLink && isTabPressed) ||
      (isFirstLink && isTabPressed && isShiftPressed) ||
      isEscPressed
    ) {
      return this.closeSubNav(panel, parent);
    }
  }

  openSubNav(panel, parent) {
    this.isIddle = true;
    this.closeAllSubNavs();
    this.subMenuOpen = true;

    const linkInstance = parent.querySelector(SELECTORS.subNavLink);

    panel.removeAttribute("hidden");
    parent.classList.add(CLASSES.active);
    linkInstance.setAttribute("aria-expanded", "true");

    setTimeout(() => {
      this.isIddle = false;
    }, 80);

    return this;
  }

  closeSubNav(panel, parent) {
    this.subMenuOpen = false;
    this.isIddle = false;

    const linkInstance = parent.querySelector(SELECTORS.subNavLink);
    const activeLink = parent.querySelector(SELECTORS.subNavItemActiveLink);
    const linkList = parent.querySelectorAll(SELECTORS.subNavItemLink);

    if (activeLink !== linkList[0]) {
      activeLink.classList.remove(CLASSES.subNavItemActiveLink);
      linkList[0].classList.add(CLASSES.subNavItemActiveLink);
    }

    panel.setAttribute("hidden", "");
    parent.classList.remove(CLASSES.active);
    linkInstance.setAttribute("aria-expanded", "false");
    parent.focus();

    return this;
  }

  handleClickOutside({ target }) {
    const isClickInside = this.context.contains(target);

    if (!isClickInside && this.subMenuOpen) {
      this.closeAllSubNavs();
    }
  }

  closeAllSubNavs() {
    const { subNavItems } = this;

    subNavItems.forEach((el) => {
      if (el.classList.contains(CLASSES.active)) {
        const activeSubMenu = el.querySelector(SELECTORS.subNavPanel);
        this.closeSubNav(activeSubMenu, el);
      }
    });
  }

  handleKeydown(event, panel, parent) {
    const currentLink = parent.querySelector(SELECTORS.subNavItemActiveLink);

    if (isEventKey(event, "space") || isEventKey(event, "enter"))
      setTimeout(() => {
        this.isIddle = false;
        currentLink.focus();
      }, 100);
    else if (isEventKey(event, "down")) this.moveVertically(currentLink, 1);
    else if (isEventKey(event, "up")) this.moveVertically(currentLink, -1);
    else if (isEventKey(event, "right")) this.moveRight(currentLink);
    else if (isEventKey(event, "left")) this.moveLeft(currentLink);
  }

  moveVertically(currentLink, step) {
    const parent = currentLink.parentElement;
    const siblings = this.getSiblings(parent);
    const currentListIndex = siblings.indexOf(parent);
    let nextListIndex = currentListIndex + step;

    // Handle assigning next element when at start, or end of list.
    if (currentListIndex < 0) {
      return;
    }
    if (nextListIndex < 0) {
      nextListIndex = siblings.length - 1;
    } else if (nextListIndex >= siblings.length) {
      nextListIndex = 0;
    }

    const next = siblings[nextListIndex];
    const nextLink = next.querySelector(SELECTORS.subNavItemLink);

    if (!nextLink) return;

    this.setFocus(currentLink, nextLink);
  }

  moveRight(currentLink) {
    const nextElement = currentLink.nextElementSibling;

    if (nextElement && nextElement.tagName == "UL") {
      const nextLink = nextElement.querySelector(SELECTORS.subNavItemLink);

      if (!nextLink) return;

      this.setFocus(currentLink, nextLink);
    }
  }

  moveLeft(currentLink) {
    const parentLI = currentLink.parentElement;
    const parentUL = parentLI.parentElement;

    const previousElement = parentUL.previousElementSibling;

    if (previousElement && previousElement.tagName == "A") {
      this.setFocus(currentLink, previousElement);
    }
  }

  /**
   *
   * @param {*} currentLink
   * @param {*} nextLink
   */
  setFocus(currentLink, nextLink) {
    currentLink.classList.remove(CLASSES.subNavItemActiveLink);

    if (!nextLink.classList.contains(CLASSES.subNavItemActiveLink))
      nextLink.classList.add(CLASSES.subNavItemActiveLink);

    nextLink.focus();
  }

  getSiblings(e) {
    const siblings = [];

    if (!e.parentNode) return siblings;

    let sibling = e.parentNode.firstChild;

    while (sibling) {
      if (sibling.nodeType === 1) {
        siblings.push(sibling);
      }

      sibling = sibling.nextSibling;
    }

    return siblings;
  }
}

export { SubNav };
